import React, { useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { BackToHomeLink, DocLink } from '../../../components/docComponents'
import AdminContent from '../../../components/adminContent';
import FloatingContainer from '../../../components/floatingContainer'
import useDocumentTitle from '../../../hooks/useDocumentTitle';

const DocsEventStagesPage = () => {

  useDocumentTitle("Event Stages | Documentation")
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const renderContent = () => {
    return (
      <FloatingContainer className="ph-20 bg-white" verticalPadding={20} maxWidth={1000}>
        <BackToHomeLink/>
        <div className="fw-700 fs-24 mt-20">Event Stages</div>
        <div className="fw-500 fs-16 c-text-gray">Assign a stage to each of your events and move them through your workflow.</div>
        <div className="b-border mv-20"></div>
        <div className="fw-700 fs-18">Overview</div>
        <div className="fw-500 mb-15 line-1-4 mt-15 bg-gray p-15 radius-8">
          This feature is way for you to organize your events based on stages. When you create an event, you can assign it an initial stage and then update it as you complete certain milestones. For example, if you just booked an event and still need your client to sign a contract, you might create a stage called "Contract Sent". Once the event is officially booked, you might move it into a stage called "Planning". You can customize your list of stages based on your specific workflow.
        </div>
        <div className="b-border mv-20"></div>
        <div className="video-responsive mh-20">
          <iframe
            width="560"
            height="315"
            src={`https://www.youtube.com/embed/_modzQ0laoE?si=Of7o__jYzbCUoJHS`}
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            title="Event Stages | Getting Started"
          />
        </div>
        <div className="b-border mv-20"></div>
        <div className="fw-700 fs-18 mb-15">Adding a Stage</div>
        <div className="mb-15">
          <ol>
            <li className="mb-15">
              Go to the <DocLink to="/admin/setup/event-settings?tab=event-stages">Event Stages</DocLink> page under <b>Setup</b>.
            </li>
            <li className="mb-15">
              Select the <b>Add</b> button.
            </li>
            <li className="mb-15">
              Fill out the following fields based on your preferences:
              <ul>
                <li className="mb-15 mt-15">
                  <b>Stage Name</b> 
                  <br/>
                  The name that will be displayed for the stage.
                </li>
                <li className="mb-15 mt-15">
                  <b>Color</b> 
                  <br/>
                  This is the color of the badge that will display on each of your events that are assigned this stage.
                </li>
                <li className="mb-15 mt-15">
                  <b>Hide on events page</b> 
                  <br/>
                  With this option selected, this stage and all events assigned to this stage will not display on the <b>Events</b> page. Only admins will have the ability to temporarily show/hide this stage when viewing events.
                </li>
              </ul>
            </li>
            <li className="mb-15">
              Once you are finished, click the <b>Save</b> button.
            </li>
            <li className="mb-15">
              Drag and drop the stage in your list to change the order.
            </li>
          </ol>
          <div className="ml-20 mt-20">
            <button className="small-primary-button mb-5" onClick={() => navigate("/admin/setup/event-settings?tab=event-stages")}>Go to Event Stages</button>
          </div>
        </div>

        <div className="b-border mv-20"></div>
        <div className="fw-700 fs-18 mb-15">Updating a Stage</div>
        <div className="mb-15">
          <ol>
            <li className="mb-15">
              Go to the <DocLink to="/admin/setup/event-settings?tab=event-stages">Event Stages</DocLink> page under <b>Setup</b>.
            </li>
            <li className="mb-15">
              Select the <b>options icon</b> next to a stage and choose <b>Edit Event Stage</b>.
            </li>
            <li className="mb-15">
              Update the following fields based on your preferences:
              <ul>
                <li className="mb-15 mt-15">
                  <b>Stage Name</b> 
                  <br/>
                  The name that will be displayed for the stage.
                </li>
                <li className="mb-15 mt-15">
                  <b>Color</b> 
                  <br/>
                  This is the color of the badge that will display on each of your events that are assigned this stage.
                </li>
                <li className="mb-15 mt-15">
                  <b>Hide on events page</b> 
                  <br/>
                  With this option selected, this stage and all events assigned to this stage will not display on the <b>Events</b> page. Only admins will have the ability to temporarily show/hide this stage when viewing events. This can be useful if you have stages such as "Archived" or "Cancelled" and don't want these events to show in your active workflow.
                </li>
              </ul>
            </li>
            <li className="mb-15">
              Once you are finished, click the <b>Save</b> button.
            </li>
          </ol>
          <div className="ml-20 mt-20">
            <button className="small-primary-button mb-5" onClick={() => navigate("/admin/setup/event-settings?tab=event-stages")}>Go to Event Stages</button>
          </div>
        </div>

        <div className="b-border mv-20"></div>
        <div className="fw-700 fs-18 mb-15">Removing a Stage</div>
        <div className="mb-15">
          <ol>
            <li className="mb-15">
              Go to the <DocLink to="/admin/setup/event-settings?tab=event-stages">Event Stages</DocLink> page under <b>Setup</b>.
            </li>
            <li className="mb-15">
              Select the <b>options icon</b> next to a stage and choose <b>Delete Event Stage</b>.
            </li>
            <li className="mb-15">
              When a dialog displays, confirm your action.
              <div className="bg-gray pv-10 ph-15 radius-8 mt-10"><b>Note:</b> event stages can only be removed if they aren't currently being used. If any events have this stage assigned, you must re-assign them to a different stage. You can also rename a stage as well if you want to repurpose it.</div>
            </li>
          </ol>
          <div className="ml-20 mt-20">
            <button className="small-primary-button mb-5" onClick={() => navigate("/admin/setup/event-settings?tab=event-stages")}>Go to Event Stages</button>
          </div>
        </div>

        <div className="b-border mv-20"></div>
        <div className="fw-700 fs-18 mb-15">Updating an Event's Stage</div>
        <div className="mb-15">
          <div className="mb-15">You can update a stage for an event in one of several ways:</div>
          <div className="mb-15 fw-700">From the Events page <span className="fw-500">(website)</span></div>
          <ol>
            <li className="mb-15">
              Go to the <DocLink to="/admin/events">Events</DocLink> page.
            </li>
            <li className="mb-15">
              Select the <b>options icon</b> next to an event and choose <b>Update Stage</b>.
            </li>
            <li className="mb-15">
              Select a new stage from the list and click <b>Save</b>.
            </li>
          </ol>
          <div className="mb-15 fw-700">From the Event Details page <span className="fw-500">(website)</span></div>
          <ol>
            <li className="mb-15">
              Go to the <DocLink to="/admin/events">Events</DocLink> page and select an event.
            </li>
            <li className="mb-15">
              Under the <b>Overview</b> tab, select the <b>edit icon</b> on the <b>Stage</b> section.
            </li>
            <li className="mb-15">
              Select a new stage from the list and click <b>Save</b>.
            </li>
          </ol>
          <div className="mb-15 fw-700">From the Event Details page <span className="fw-500">(mobile app)</span></div>
          <ol>
            <li className="mb-15">
              Go to the <b>Events</b> tab in your app and select an event.
            </li>
            <li className="mb-15">
              Under the <b>Overview</b> tab, select <b>Update Stage</b>.
            </li>
            <li className="mb-15">
              Select a new stage from the list.
            </li>
          </ol>
          <div className="bg-gray pv-10 ph-15 radius-8 mt-10"><b>Note:</b> event stages can only be updated by admins or staff members who have the "Edit event details" permission.</div>
          <div className="ml-20 mt-20">
            <button className="small-primary-button mb-5" onClick={() => navigate("/admin/events")}>Go to Events</button>
          </div>
        </div>
      </FloatingContainer>
    )
  }

  return (
    <AdminContent body={renderContent()}/>
  );
}

export default DocsEventStagesPage;
